import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  useRestaurants,
  useCreateRestaurant,
  useUpdateRestaurant,
  useDeleteRestaurant,
} from '@/hooks/useRestaurants';
import { Restaurant } from '@/lib/supabase';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ClientForm } from './ClientForm';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  AlertCircle,
  FileText,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const ClientsTable: React.FC = () => {
  const { data: clients, isLoading, error } = useRestaurants();
  const createMutation = useCreateRestaurant();
  const updateMutation = useUpdateRestaurant();
  const deleteMutation = useDeleteRestaurant();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Restaurant | null>(null);

  // Gestion de l'ajout
  const handleCreate = async (data: Partial<Restaurant>) => {
    try {
      await createMutation.mutateAsync(data);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de la création:', error);
    }
  };

  // Gestion de l'édition
  const handleUpdate = async (data: Partial<Restaurant>) => {
    if (!selectedClient) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedClient.id,
        updates: data,
      });
      setIsEditModalOpen(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  // Gestion de la suppression
  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      await deleteMutation.mutateAsync(selectedClient.id);
      setIsDeleteModalOpen(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  // États de chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-stratos-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <AlertCircle className="w-6 h-6 text-red-600 mb-2" />
        <p className="text-red-800">Erreur lors du chargement des clients</p>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      active: { variant: 'success', text: 'Actif' },
      prospect: { variant: 'default', text: 'Prospect' },
      paused: { variant: 'warning', text: 'En pause' },
      churned: { variant: 'danger', text: 'Churn' },
    };
    return variants[status] || variants.prospect;
  };

  const getPackBadge = (pack: string | null) => {
    if (!pack) return null;
    const colors: Record<string, string> = {
      essentiel: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      signature: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <Badge className={colors[pack] || 'bg-gray-100 text-gray-800'}>
        {pack.charAt(0).toUpperCase() + pack.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Gestion des Clients
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {clients?.length || 0} client{clients && clients.length > 1 ? 's' : ''} enregistré
            {clients && clients.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-stratos-gold hover:bg-stratos-gold-dark text-gray-900"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Client
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Restaurant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pack
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenu/mois
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {clients?.map((client, index) => {
                const statusBadge = getStatusBadge(client.status);
                return (
                  <motion.tr
                    key={client.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition"
                  >
                    {/* Restaurant */}
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-stratos-gold/10 rounded-lg p-2">
                          <Building2 className="w-5 h-5 text-stratos-gold" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {client.location || 'Non renseigné'}
                          </div>
                          {client.cuisine && client.cuisine.length > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              {client.cuisine.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {(client.primary_contact as any)?.name && (
                          <div className="font-medium text-gray-900 mb-1">
                            {(client.primary_contact as any).name}
                          </div>
                        )}
                        {(client.primary_contact as any)?.role && (
                          <div className="text-xs text-gray-500 mb-2">
                            {(client.primary_contact as any).role}
                          </div>
                        )}
                        {client.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            <a
                              href={`mailto:${client.email}`}
                              className="hover:text-stratos-gold"
                            >
                              {client.email}
                            </a>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            {client.phone}
                          </div>
                        )}
                        {client.website && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Globe className="w-3 h-3" />
                            <a
                              href={client.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-stratos-gold"
                            >
                              Site web
                            </a>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Pack */}
                    <td className="px-6 py-4">
                      {getPackBadge(client.pack)}
                    </td>

                    {/* Statut */}
                    <td className="px-6 py-4">
                      <Badge variant={statusBadge.variant}>
                        {statusBadge.text}
                      </Badge>
                    </td>

                    {/* Revenu */}
                    <td className="px-6 py-4">
                      {client.monthly_revenue ? (
                        <span className="font-semibold text-gray-900">
                          {client.monthly_revenue.toLocaleString('fr-FR')} €
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Lien Plan d'action pour Café Max */}
                        {client.slug === 'cafe-max' && (
                          <Link to="/cafe-max-contract">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-stratos-gold border-stratos-gold hover:bg-stratos-gold/10"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Plan d'action
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedClient(client);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedClient(client);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:bg-red-50 hover:border-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {clients && clients.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun client
            </h3>
            <p className="text-gray-600 mb-4">
              Commencez par ajouter votre premier client
            </p>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-stratos-gold hover:bg-stratos-gold-dark text-gray-900"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un client
            </Button>
          </div>
        )}
      </div>

      {/* Modal Ajout */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent>
          <DialogHeader onClose={() => setIsAddModalOpen(false)}>
            <DialogTitle>Nouveau Client</DialogTitle>
            <DialogDescription>
              Ajoutez un nouveau restaurant client à votre portefeuille
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            onSubmit={handleCreate}
            onCancel={() => setIsAddModalOpen(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Édition */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader onClose={() => setIsEditModalOpen(false)}>
            <DialogTitle>Modifier le Client</DialogTitle>
            <DialogDescription>
              Mettez à jour les informations du restaurant
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            client={selectedClient}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedClient(null);
            }}
            isLoading={updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Suppression */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader onClose={() => setIsDeleteModalOpen(false)}>
            <DialogTitle>Supprimer le Client</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Toutes les données associées seront supprimées.
            </DialogDescription>
          </DialogHeader>
          {selectedClient && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
              <p className="text-red-800 font-medium">
                Êtes-vous sûr de vouloir supprimer{' '}
                <strong>{selectedClient.name}</strong> ?
              </p>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedClient(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

